import { LoadState, useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import { MarketingForgeElementType } from "@redotech/redo-model/forge/marketing-signup-forge-types";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import {
  parseLocalData,
  SignupFormLocalData,
} from "@redotech/redo-web/marketing/signup-form/signup-form-instance";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { useCallback, useContext, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  RedoShopifyRpcClientContext,
  RedoShopifyRpcClientContextProvider,
} from "../client/redo-shopify-rpc-client";
import { log } from "../log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";

const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Marketing embedded form");

  const elements = document.querySelectorAll(
    'div[id^="redo-marketing-embedded-form-"]',
  );

  const matchingElements = Array.from(elements).filter((element) => {
    return /^[a-zA-Z0-9]{5}$/.test(element.id.split("-").pop() ?? "");
  });

  log("Found Marketing embedded form container");

  if (matchingElements.length === 0 || !matchingElements[0]) {
    return;
  }

  // Shopify applies display: none to empty divs
  (matchingElements[0] as HTMLElement).style.display = "unset";

  const root = createRoot(matchingElements[0]);
  root.render(
    <StyleRoot container={matchingElements[0]} styleManager={STYLE_MANAGER}>
      <RedoRoot
        extensionShopifyClient={undefined}
        storeUrl={
          window.redoStorefront?.storeUrl ??
          window.Shopify?.shop ??
          window.Shopify?.Checkout?.apiHost
        }
      >
        <RedoShopifyRpcClientContextProvider
          baseUrl={process.env.REDO_SHOPIFY_SERVER_URL!}
          storeUrl={window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost}
          widgetId={window.redoWidgetId}
        >
          <RedoMarketingEmbeddedForm
            autocheck={autocheck}
            formDivId={matchingElements[0].id}
          />
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );
}

function useFormData(
  formsLoad: LoadState<
    Awaited<ReturnType<RedoShopifyRpcClient["getEmbeddedForms"]>>
  >,
  rpcClient: RedoShopifyRpcClient | undefined,
  customerHash: string | undefined,
  formDivId: string,
): { form: ISignupForm | undefined; formId: string | undefined } {
  const [form, setForm] = useState<ISignupForm | undefined>(undefined);
  const [formId, setFormId] = useState<string | undefined>(undefined);

  if (formsLoad.value && rpcClient && customerHash && !form) {
    const matchingForm = formsLoad.value.find((f) =>
      f.form?.behavior.embeddedCode.includes(formDivId),
    );

    if (matchingForm) {
      log("Form found", matchingForm);
      void rpcClient
        .logSignupFormAnalytics({
          idempotencyKey: customerHash,
          formId: matchingForm.id,
          type: "view",
        })
        .catch((error) => {
          console.error("Error logging form view:", error);
        });

      setForm(matchingForm.form);
      setFormId(matchingForm.id);
    }
  }

  return { form, formId };
}

function RedoMarketingEmbeddedForm({
  autocheck,
  formDivId,
}: {
  autocheck: WidgetAutocheck;
  formDivId: string;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useState<SignupFormLocalData>(() => {
    const stored = localStorage.getItem(SIGNUP_LOCAL_STORAGE_KEY);
    return stored ? parseLocalData(stored) : {};
  });

  const formsLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient.getEmbeddedForms({});
    return forms.filter((f) => f.publishedAt);
  }, []);

  const { form, formId } = useFormData(
    formsLoad,
    rpcClient,
    localData.customerHash,
    formDivId,
  );

  const getNextStepIndex = useCallback(
    (startIndex: number): number => {
      if (!form) return startIndex;
      const skipPhoneSteps = true;
      let stepIndex = startIndex;

      while (
        skipPhoneSteps &&
        stepIndex < form?.steps.length &&
        form.steps[stepIndex]?.elements.some(
          (el) =>
            el.type === MarketingForgeElementType.INPUT &&
            el.inputType === "phone",
        )
      ) {
        stepIndex++;
      }

      return stepIndex;
    },
    [form],
  );

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      if (!form || !formId || !rpcClient) return;
      const result = await rpcClient.submitSignupFormStep({
        formId,
        stepIndex: i,
        formData,
        customerId,
        entryPoint: MarketingEntryPoint.SIGNUP_FORM,
        anonymousId: localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY)!,
      });

      if (!result.success) {
        console.error("Failed to submit form step", result);
        return;
      }

      if (result.customerId) {
        setCustomerId(result.customerId);
      }

      if (!localData.atLeastOneStepSubmittedAt && localData.customerHash) {
        setLocalData((prevData) => ({
          ...prevData,
          atLeastOneStepSubmittedAt: new Date(),
        }));
        await rpcClient.logSignupFormAnalytics({
          idempotencyKey: localData.customerHash,
          formId,
          type: "submit",
        });
      }

      const nextStepIndex = getNextStepIndex(i + 1);
      const hasNextStep = nextStepIndex < form.steps.length;

      if (hasNextStep) {
        setCurrentStep(nextStepIndex);
      } else {
        setCurrentStep(0);
      }
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rpcClient, customerId, localData, getNextStepIndex, form, formId],
  );

  if (formsLoad.pending) {
    return null;
  }

  if (formsLoad.error) {
    return null;
  }

  if (!form) {
    return null;
  }

  return (
    <SignupForm
      brandKit={autocheck.brandKit}
      currentStep={currentStep}
      form={form}
      onSubmitStep={submitStep}
    />
  );
}
